import { useRef, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Plus from 'assets/icons/plus';
import { getActive } from 'utils/helpers/get/getActive';
import Filter from 'components/table/filters/filter';
import { activeOptions } from 'utils/helpers/constVariables';
import SimpleSelect from 'ui/select';
import { useMeRole } from 'store/hooks/useMeRole';
import TableFilter from 'components/table/filters';
import { useCustomSearchParams } from '../../../users/lib/useParams';
import Search from 'ui/search';
import { Table } from '@tanstack/react-table';
import { ObjectEntityFragment } from 'pages/facilities/api/fragments/generated/ObjectEntity.fragment';
import ColumnSettings from 'components/table/columnSettings';
import { Role } from 'generated/graphql';
import { useGetCitiesQuery } from 'graphql/queries/generated/GetCities';
import { useGetNaimixLegalEntitiesQuery } from 'pages/facility/api/queries/generated/GetNaimixLegalEntities';
import { useUsersByRole } from 'utils/api/useUsersByRole';
import debounce from 'lodash/debounce';

interface Props {
  table: Table<ObjectEntityFragment>;
}

const findUserById = (users: any[] | undefined, id: string | null) => {
  if (!users || !id) return undefined;
  return users.find(user => user.id === id);
};

const FacilitiesFilter = ({ table }: Props) => {
  const { handleParams, params, setParams, useParamsCount, resetFilter } = useCustomSearchParams();

  const cityParam = params.get('cityId');
  const foremanParam = params.get('foremanId');
  const supervisorParam = params.get('supervisorId');
  const legalEntityParam = params.get('naimixLegalEntityId');
  const activeParam = params.get('active');

  const [isActive, setIsActive] = useState<string | null>(activeParam);
  const [cityId, setCityId] = useState<string | null>(cityParam);
  const [foremanId, setForemanId] = useState<string | null>(foremanParam);
  const [supervisorId, setSupervisorId] = useState<string | null>(supervisorParam);
  const [legalEntityId, setLegalEntityId] = useState<string | null>(legalEntityParam);
  const [showFilter, setShowFilter] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const debouncedHandleScrollRef = useRef<ReturnType<typeof debounce>>();

  const ITEMS_PER_PAGE = 100;

  const { data: citiesData, loading: citiesLoading } = useGetCitiesQuery({
    variables: {
      input: {
        limit: ITEMS_PER_PAGE,
        offset: 0,
      },
    },
  });

  const { data: legalEntitiesData, loading: legalEntitiesLoading } = useGetNaimixLegalEntitiesQuery({
    variables: {
      input: {
        limit: ITEMS_PER_PAGE,
        offset: 0,
      },
    },
  });

  const paramsCount = useParamsCount();
  const btnRef = useRef<HTMLButtonElement>(null);
  const isMeAdmin = useMeRole([Role.Admin]);

  const {
    users: foremen,
    loading: foremenLoading,
    loadMore: loadMoreForemen,
  } = useUsersByRole({
    role: Role.Foreman,
    limit: 20,
    offset: 0,
    selectedUserId: foremanId ?? undefined,
  });

  const {
    users: supervisors,
    loading: supervisorsLoading,
    loadMore: loadMoreSupervisors,
  } = useUsersByRole({
    role: Role.Supervisor,
    limit: 20,
    offset: 0,
    selectedUserId: supervisorId ?? undefined,
  });

  useEffect(() => {
    debouncedHandleScrollRef.current = debounce(async (loadMore: () => Promise<void>) => {
      if (isLoadingMore) return;
      setIsLoadingMore(true);
      try {
        await loadMore();
      } catch (error) {
        console.error('Failed to load more:', error);
      } finally {
        setIsLoadingMore(false);
      }
    }, 300);

    return () => {
      debouncedHandleScrollRef.current?.cancel();
    };
  }, [isLoadingMore]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>, loadMore: () => Promise<void>, isLoading: boolean) => {
    const target = e.target as HTMLDivElement;
    const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

    if (scrollBottom < 50 && !isLoading) {
      debouncedHandleScrollRef.current?.(loadMore);
    }
  };

  const selectedForeman = useMemo(() => findUserById(foremen, foremanParam), [foremen, foremanParam]);

  const selectedSupervisor = useMemo(() => findUserById(supervisors, supervisorParam), [supervisors, supervisorParam]);

  const selectedCity = useMemo(
    () => citiesData?.getCities.cities.find(c => c.id === cityParam),
    [citiesData?.getCities.cities, cityParam],
  );

  const selectedLegalEntity = useMemo(
    () => legalEntitiesData?.getNaimixLegalEntities.naimixLegalEntities.find(l => l.id === legalEntityParam),
    [legalEntitiesData?.getNaimixLegalEntities.naimixLegalEntities, legalEntityParam],
  );

  return (
    <div className="filter-bar flex xs:flex-row flex-col gap-y-2 justify-between">
      <div className="flex flex-col gap-y-2">
        <div className={'flex relative gap-x-2'}>
          <Search />
          <button
            type="button"
            ref={btnRef}
            className="btn-stroke flex gap-x-1 items-center"
            onClick={() => {
              setShowFilter(value => !value);
            }}
          >
            Фильтр
            {Boolean(paramsCount) && (
              <span className="Button3 text-primary rounded-full bg-primary bg-opacity-20 px-[7.5px]">
                {String(paramsCount)}
              </span>
            )}
          </button>
          <ColumnSettings<ObjectEntityFragment> table={table} />
          <TableFilter show={showFilter} setShow={setShowFilter} triggerRef={btnRef}>
            <SimpleSelect
              label="Город"
              popupClassName="cities-dropdown"
              onChange={value => setCityId(value)}
              value={cityId}
              allOption={'Все города'}
              placeholder={'Не выбрано'}
              options={citiesData?.getCities.cities}
              loading={citiesLoading}
            />

            <SimpleSelect
              label="Бригадир"
              onChange={value => setForemanId(value)}
              value={foremanId}
              allOption={'Все бригадиры'}
              placeholder={'Не выбрано'}
              options={foremen?.map(user => ({
                id: user.id,
                name: user.lastname,
              }))}
              loading={foremenLoading}
              onPopupScroll={e => handleScroll(e, loadMoreForemen, foremenLoading)}
              listHeight={200}
            />

            <SimpleSelect
              label="Супервайзер"
              onChange={value => setSupervisorId(value)}
              value={supervisorId}
              allOption={'Все супервайзеры'}
              placeholder={'Не выбрано'}
              options={supervisors?.map(user => ({
                id: user.id,
                name: user.lastname,
              }))}
              loading={supervisorsLoading}
              onPopupScroll={e => handleScroll(e, loadMoreSupervisors, supervisorsLoading)}
              listHeight={200}
            />

            <SimpleSelect
              label="Юр. лицо в Наймикс"
              popupClassName="legal-entities-dropdown"
              onChange={value => setLegalEntityId(value)}
              value={legalEntityId}
              allOption={'Все юр. лица'}
              placeholder={'Не выбрано'}
              options={legalEntitiesData?.getNaimixLegalEntities.naimixLegalEntities}
              loading={legalEntitiesLoading}
            />

            <SimpleSelect
              label="Статус активности"
              onChange={value => setIsActive(value)}
              value={isActive}
              allOption="Все"
              placeholder="Не выбрано"
              options={activeOptions}
            />

            <div className="flex gap-x-5">
              <button
                className="btn-primary"
                onClick={() => {
                  handleParams('cityId', cityId);
                  handleParams('foremanId', foremanId);
                  handleParams('supervisorId', supervisorId);
                  handleParams('naimixLegalEntityId', legalEntityId);
                  handleParams('active', isActive);
                  params.delete('page');
                  setParams(params);
                  setShowFilter(false);
                }}
              >
                <span>Применить</span>
              </button>
              <button
                className="btn-stroke xs:w-auto w-full"
                onClick={() => {
                  setCityId(null);
                  setForemanId(null);
                  setSupervisorId(null);
                  setLegalEntityId(null);
                  setIsActive(null);
                  setParams();
                  setShowFilter(false);
                }}
              >
                Сбросить
              </button>
            </div>
          </TableFilter>
        </div>

        {!!paramsCount && (
          <div className="flex gap-2">
            <Filter
              show={!!cityParam}
              content={selectedCity?.name}
              clickHandler={() => {
                setCityId(null);
                resetFilter('cityId');
              }}
            />

            <Filter
              show={!!foremanParam}
              content={selectedForeman?.lastname}
              clickHandler={() => {
                setForemanId(null);
                resetFilter('foremanId');
              }}
            />

            <Filter
              show={!!supervisorParam}
              content={selectedSupervisor?.lastname}
              clickHandler={() => {
                setSupervisorId(null);
                resetFilter('supervisorId');
              }}
            />

            <Filter
              show={!!legalEntityParam}
              content={selectedLegalEntity?.name}
              clickHandler={() => {
                setLegalEntityId(null);
                resetFilter('naimixLegalEntityId');
              }}
            />

            <Filter
              show={!!activeParam}
              content={getActive(activeParam)}
              clickHandler={() => {
                setIsActive(null);
                resetFilter('active');
              }}
            />
          </div>
        )}
      </div>
      {isMeAdmin && (
        <Link to="/facilities/create">
          <button type="button" className="btn-primary_big flex items-center gap-x-2">
            <Plus className="text-smena_white" />
            Новый объект
          </button>
        </Link>
      )}
    </div>
  );
};

export default FacilitiesFilter;
